import { useEffect, useState } from 'react'
import { KeyRound, ScanLine, Shield } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { AdminAttendanceDialog } from '@/pages/attendance/punch-dialog'
import { WorkerProfileLink } from '@/pages/attendance/worker-link'
import {
  useAttendanceDirectory,
  useAttendancePunchActions,
  workerCode,
  workerName,
} from '@/pages/attendance/data'
import { getErrorMessage } from '@/lib/api'
import { isCardScan, isLegacyWorkerQr, parseWorkerScan } from '@/lib/worker-id-card'
import { workersApi } from '@/services/workers'
import type { Worker } from '@/types'

type Mode = 'scan' | 'pin'

export default function ClockStationPage() {
  const [mode, setMode] = useState<Mode>('scan')
  const [pin, setPin] = useState('')
  const [cardCode, setCardCode] = useState('')
  const [now, setNow] = useState(() => new Date())
  const [matched, setMatched] = useState<Worker | null>(null)
  const [punchOpen, setPunchOpen] = useState(false)
  const [punchOnline, setPunchOnline] = useState(true)
  const { workers } = useAttendanceDirectory()
  const { record } = useAttendancePunchActions()

  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 1000)
    return () => window.clearInterval(timer)
  }, [])

  const findWorker = (code: string) => {
    const normalized = parseWorkerScan(code)
    if (!normalized) return null
    return workers.find((worker) => workerCode(worker) === normalized)
      || workers.find((worker) => workerCode(worker).endsWith(normalized) && normalized.length >= 4)
      || null
  }

  const [resolvingScan, setResolvingScan] = useState(false)
  const submitScan = async (value: string) => {
    if (isLegacyWorkerQr(value)) {
      toast.error('This ID card is outdated. Download a new card for the employee.')
      setCardCode('')
      return
    }
    let worker: Worker | null = null
    if (isCardScan(value)) {
      setResolvingScan(true)
      try {
        const card = await workersApi.resolveIdCard(value.trim())
        worker = workers.find((row) => String(row.worker_id ?? row.id) === String(card.worker_id))
          ?? workers.find((row) => String(row.id) === String(card.worker_id))
          ?? null
      } catch (error) {
        toast.error(getErrorMessage(error, 'This ID card is no longer valid.'))
        setCardCode('')
        return
      } finally {
        setResolvingScan(false)
      }
    } else {
      worker = findWorker(value)
    }
    if (!worker) {
      toast.error('No employee matches that card code')
      setCardCode('')
      return
    }
    openPunch(worker, !(worker.is_online ?? worker.isOnline))
  }

  const openPunch = (worker: Worker, online: boolean) => {
    setMatched(worker)
    setPunchOnline(online)
    setPunchOpen(true)
  }

  return (
    <div className="mx-auto max-w-xl space-y-4 pb-6">
      <div className="text-center">
        <div className="text-3xl font-semibold tabular-nums text-slate-900">
          {now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </div>
        <div className="text-sm text-slate-500">
          {now.toLocaleDateString([], { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
        </div>
      </div>

      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-5 grid grid-cols-2 gap-2 rounded-xl bg-slate-50 p-1">
          <Button type="button" variant={mode === 'scan' ? 'default' : 'ghost'} onClick={() => setMode('scan')}>
            <ScanLine className="mr-2 h-4 w-4" />
            Scan Card
          </Button>
          <Button type="button" variant={mode === 'pin' ? 'default' : 'ghost'} onClick={() => { setMode('pin'); setMatched(null) }}>
            <KeyRound className="mr-2 h-4 w-4" />
            Enter PIN
          </Button>
        </div>

        {matched ? (
          <div className="space-y-3 text-center">
            <h2 className="text-xl font-semibold text-slate-900">
              Welcome, <WorkerProfileLink workerId={matched.worker_id ?? matched.id} name={workerName(matched)} />
            </h2>
            <p className="text-sm text-slate-500">{workerCode(matched)}</p>
            <div className="text-xs uppercase tracking-wide text-slate-500">Record attendance</div>
            <div className="text-2xl font-semibold tabular-nums">{now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
            <div className="flex justify-center gap-2">
              <Button type="button" onClick={() => openPunch(matched, true)}>Check in</Button>
              <Button type="button" variant="outline" onClick={() => openPunch(matched, false)}>Check out</Button>
            </div>
          </div>
        ) : mode === 'scan' ? (
          <form
            className="space-y-3 text-center"
            onSubmit={(event) => {
              event.preventDefault()
              void submitScan(cardCode)
            }}
          >
            <ScanLine className="mx-auto h-10 w-10 text-blue-600" />
            <h2 className="text-lg font-semibold text-slate-900">Scan employee QR / barcode</h2>
            <p className="text-sm text-slate-500">Scan the QR on the employee ID card with a scanner, or type the ID printed on the card.</p>
            <Input
              value={cardCode}
              onChange={(event) => setCardCode(event.target.value)}
              placeholder="Scan or enter employee ID"
              aria-label="Employee ID or card scan"
              className="text-center"
              autoFocus
            />
            <Button type="submit" disabled={!cardCode.trim() || resolvingScan}>Continue</Button>
          </form>
        ) : (
          <form
            className="space-y-3 text-center"
            onSubmit={(event) => {
              event.preventDefault()
              const worker = findWorker(pin)
              if (!worker) {
                toast.error('No employee matches that PIN')
                return
              }
              openPunch(worker, !(worker.is_online ?? worker.isOnline))
            }}
          >
            <KeyRound className="mx-auto h-10 w-10 text-blue-600" />
            <h2 className="text-lg font-semibold text-slate-900">Enter your PIN</h2>
            <Input
              value={pin}
              inputMode="numeric"
              maxLength={6}
              onChange={(event) => setPin(event.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="••••"
              className="text-center text-2xl tracking-[0.4em]"
            />
            <Button type="submit" disabled={pin.length < 4}>Continue</Button>
          </form>
        )}
      </section>

      <AdminAttendanceDialog
        open={punchOpen}
        onOpenChange={setPunchOpen}
        workers={matched ? [matched] : workers}
        workerId={matched ? String(matched.worker_id ?? matched.id) : ''}
        lockWorker
        initialStatus={punchOnline}
        isOnline={Boolean(matched?.is_online ?? matched?.isOnline)}
        isPending={record.isPending}
        onSubmit={async (payload) => {
          const result = await record.mutateAsync(payload)
          const note = (result as { punch_note?: string; punch_applied?: boolean } | undefined)?.punch_note
          if (note && (result as { punch_applied?: boolean })?.punch_applied === false) {
            throw new Error(note)
          }
          toast.success(payload.status ? `${workerName(matched)} checked in` : `${workerName(matched)} checked out`)
          setMatched(null)
          setPin('')
          setCardCode('')
        }}
      />
      <p className="flex items-start gap-2 text-xs text-slate-500">
        <Shield className="mt-0.5 h-4 w-4 shrink-0" />
        Clock credentials are validated against worker records. QR codes should contain an employee code, not personal information.
      </p>
    </div>
  )
}
